import { useFeatureFlagPayload } from "posthog-js/react";

export const INCIDENT_BANNER_FLAG_KEY = "incident-banner";

export type IncidentBannerSeverity = "info" | "warning" | "caution";

export type IncidentBanner = {
  message: string;
  severity: IncidentBannerSeverity;
};

const VALID_SEVERITIES: IncidentBannerSeverity[] = ["info", "warning", "caution"];

/**
 * Validates the raw PostHog flag payload for the incident banner. Kept separate
 * from the hook below so the parsing logic is testable without a PostHog
 * provider.
 */
export const parseIncidentBannerPayload = (
  payload: unknown
): IncidentBanner | null => {
  if (typeof payload !== "object" || payload === null) {
    return null;
  }

  const { enabled, message, severity } = payload as Record<string, unknown>;

  if (!enabled || typeof message !== "string" || message.trim() === "") {
    return null;
  }

  return {
    message,
    severity: VALID_SEVERITIES.includes(severity as IncidentBannerSeverity)
      ? (severity as IncidentBannerSeverity)
      : "warning",
  };
};

/**
 * Reads the "incident-banner" PostHog remote config flag payload, e.g.
 * { "enabled": true, "message": "...", "severity": "warning" }.
 * Ops can toggle this flag to communicate outages/incidents without a deploy.
 */
export const useIncidentBanner = (): IncidentBanner | null => {
  const payload = useFeatureFlagPayload(INCIDENT_BANNER_FLAG_KEY);
  return parseIncidentBannerPayload(payload);
};
